import React from "react";
import { Link } from "react-router-dom";

const ApplyOnline = () => {
  const membershipCategories = [
    {
      id: 1,
      title: "PRESS MEDIA",
      icon: "📰",
      description:
        "Print media are lightweight, portable, disposable publications printed on paper and circulated as physical copies in forms we call books, newspapers, magazines and newsletters.",
      details: [
        "Books, Newspapers, Magazines and Newsletters",
        "Informative and entertaining content",
        "General or special interest publications",
        "Published daily, weekly, monthly or quarterly",
      ],
      buttonText: "Apply for Print Media",
      link: "/apply/press-media",
      color: "from-blue-600 to-blue-700",
      bgColor: "blue",
    },
    {
      id: 2,
      title: "BROADCASTING MEDIA (TV & RADIO)",
      icon: "📺",
      description:
        "Broadcast and electronic media today are portable sources of entertainment and information distributed by wired and wireless radio and television stations and networks.",
      details: [
        "Television and Radio Stations",
        "Sound and video recordings",
        "Wired and wireless distribution",
        "Mobile Internet broadcasting",
      ],
      buttonText: "Apply for Broadcasting",
      link: "/apply/broadcasting",
      color: "from-purple-600 to-purple-700",
      bgColor: "purple",
    },
    {
      id: 3,
      title: "DIGITAL MEDIA",
      icon: "💻",
      description:
        "Digital media are Internet sites where people interact freely, sharing and discussing information about each other and their lives, using a multimedia mix of personal words, pictures, videos and audio.",
      details: [
        "Web TV and Online e-News",
        "Multimedia content sharing",
        "Interactive Internet platforms",
        "Social media and digital publications",
      ],
      buttonText: "Apply for Digital Media",
      link: "/apply/digital-media",
      color: "from-green-600 to-green-700",
      bgColor: "green",
    },
    {
      id: 4,
      title: "CITIZEN REPORTER",
      icon: "🎤",
      description:
        "Citizen reporter is derived from Citizen Journalism. Citizen reporter take up an initiative to express ideas irrespective of their educational or professional background.",
      details: [
        "No educational or professional barriers",
        "Breaking free from media bias",
        "Local news on global platform",
        "Community-driven journalism",
      ],
      buttonText: "Apply as Citizen Reporter",
      link: "/apply/citizen-reporter",
      color: "from-orange-600 to-orange-700",
      bgColor: "orange",
    },
  ];

  const getIconColor = (bgColor) => {
    const colors = {
      blue: "bg-blue-100 text-blue-600",
      purple: "bg-purple-100 text-purple-600",
      green: "bg-green-100 text-green-600",
      orange: "bg-orange-100 text-orange-600",
    };
    return colors[bgColor] || "bg-gray-100 text-gray-600";
  };

  const getDetailIconColor = (bgColor) => {
    const colors = {
      blue: "text-blue-500",
      purple: "text-purple-500",
      green: "text-green-500",
      orange: "text-orange-500",
    };
    return colors[bgColor] || "text-gray-500";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <section 
        className="relative bg-blue-900 text-white py-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/images/online_bg.jpg')" }}
      >
        <div className="absolute inset-0 bg-blue-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Online Application for Membership
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto">
              Join the Newsmedia Association of India and become part of India's
              leading media professionals network
            </p>
          </div>
        </div>
      </section>

      {/* Membership Categories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Membership Category
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select the category that best describes your media platform and
              join our growing community of media professionals
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {membershipCategories.map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 group"
              >
                {/* Header with Gradient */}
                <div className={`bg-gradient-to-r ${category.color} p-6`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div
                        className={`w-16 h-16 rounded-2xl ${getIconColor(
                          category.bgColor
                        )} flex items-center justify-center text-2xl`}
                      >
                        {category.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-white">
                        {category.title}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Description */}
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    {category.description}
                  </p>

                  {/* Features List */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-4 text-lg">
                      Includes:
                    </h4>
                    <ul className="space-y-3">
                      {category.details.map((detail, index) => (
                        <li key={index} className="flex items-start">
                          <svg
                            className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${getDetailIconColor(
                              category.bgColor
                            )}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-gray-600">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Button */}
                  <Link
                    to={category.link}
                    className={`group/btn w-full bg-gradient-to-r ${category.color} hover:opacity-90 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center`}
                  >
                    <span className="text-lg">{category.buttonText}</span>
                    <svg
                      className="w-5 h-5 ml-3 transform group-hover/btn:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Join NAI?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the exclusive benefits of being a member of India's
              premier media association
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: "🛡️",
                title: "Professional Protection",
                description:
                  "Comprehensive safety and legal protection for media professionals",
              },
              {
                icon: "🌐",
                title: "Networking Opportunities",
                description:
                  "Connect with media professionals and industry leaders across India",
              },
              {
                icon: "📚",
                title: "Training & Development",
                description:
                  "Access to specialized training programs and skill development workshops",
              },
              {
                icon: "⚖️",
                title: "Legal Support",
                description:
                  "Legal assistance and advocacy for media rights and freedom",
              },
              {
                icon: "📰",
                title: "Industry Recognition",
                description:
                  "Get recognized as a certified media professional in your field",
              },
              {
                icon: "🤝",
                title: "Collaboration",
                description:
                  "Opportunities for collaboration and partnerships within the media industry",
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow duration-300"
              >
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Join Our Community?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-blue-100">
            Take the first step towards becoming part of India's most respected
            media association. Choose your category and apply today.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Link
              to="/contact"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300"
            >
              Need Help? Contact Us
            </Link>
            <Link
              to="/faq"
              className="border-2 border-white hover:bg-white hover:text-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300"
            >
              View FAQ
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplyOnline;
